import { DialogContent, FormControl, InputLabel, MenuItem, Select } from "@material-ui/core";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import uiConf from "../../../config/uiConf";

import Dialog from "../../dialog/functional/Dialog";
import DialogActionsOk from "../../dialog/functional/DialogActionsOk";
import DialogHeader from "../../dialog/functional/DialogHeader";
import DialogTitle from "../../dialog/functional/DialogTitle";
import DialogHeaderWithClose from "../../dialog/functional/DialogHeaderWithClose";

/**
 * @COMPONENT_NAME UnitPurposeSelectDialog
 * @DESCRIPTION 단위 목적 선택을 위한 다이얼로그 컴포넌트
 *              단위 목적 리스트를 MUI Select로 표시하고 사용자가 선택할 수 있도록 함
 *              확인 버튼 클릭 시 선택된 목적을 부모 컴포넌트로 전달하고
 *              취소 또는 다이얼로그 닫기 시 선택 값을 원래 상태로 복원
 *              다이얼로그가 숨겨질 때마다 자동으로 초기화 처리
 *
 * @PROPS
 *   - {string} id - 다이얼로그 ID
 *   - {boolean} moveable - 다이얼로그 이동 가능 여부
 *   - {boolean} resizable - 다이얼로그 크기 조정 가능 여부
 *   - {boolean} fullWidth - 전체 너비 사용 여부
 *   - {string} maxWidth - 최대 너비 설정
 *   - {Function} onInitialized - 다이얼로그 초기화 시 호출되는 콜백
 *   - {Function} onConfirm - 확인 버튼 클릭 시 호출되는 콜백
 *   - {Array} purposeList - 단위 목적 리스트 (unitPurposeId, purposeName 포함)
 *   - {Object} currentPurpose - 현재 선택된 단위 목적 객체
 *
 * @STATE
 *   - {Object} instance - 다이얼로그 인스턴스 객체 (show, hide 메서드 포함)
 *   - {string} selected - 현재 선택된 단위 목적 ID
 *
 * @CALLBACKS
 *   - {Function} onDialogInitialized - 다이얼로그 초기화 시 호출되는 내부 콜백
 *   - {Function} handleConfirm - 확인 버튼 클릭 시 호출되는 내부 콜백
 *   - {Function} handleClose - 다이얼로그 닫기 시 호출되는 내부 콜백
 *
 * @EXAMPLE
 *   <UnitPurposeSelectDialog
 *     onInitialized={(instance) => (dialogRef.current = instance)}
 *     onConfirm={handleDialogConfirm}
 *     purposeList={purposeList}
 *     currentPurpose={selectedPurpose}
 *   />
 */
function UnitPurposeSelectDialog(props) {
    const { t } = useTranslation();

    const {
        id,

        // grid,
        // schema,

        moveable,
        resizable,
        fullWidth,
        maxWidth,

        onInitialized,
        onConfirm,
        purposeList,
        currentPurpose,

        // $parent,

        ...others
    } = props;

    const [instance, setInstance] = useState();
    const [selected, setSelected] = useState("");

    useEffect(() => {
        if (instance) {
            setSelected(currentPurpose?.unitPurposeId || "");
        }
    }, [currentPurpose, instance]);

    let title = t("dialog.title.grid.unitPurposeSelect");

    const onDialogInitialized = (instance) => {
        let myInstance = {
            show: () => {
                instance.show();
            },
            hide: () => {
                instance.hide();
            },
        };

        setInstance(myInstance);

        if (onInitialized) {
            onInitialized(myInstance);
        }
    };

    const handleConfirm = () => {
        const selectedPurpose = purposeList.find((p) => p.unitPurposeId === selected);
        if (onConfirm) {
            onConfirm(selectedPurpose);
        }
    };

    const handleClose = () => {
        setSelected(() => {
            return currentPurpose?.unitPurposeId || "";
        });
    };

    return (
        <Dialog
            id={id}
            data-cy={"unitPurposeSelectDialog"}
            moveable={moveable ? moveable : uiConf.dialogMoveable}
            resizable={resizable ? resizable : uiConf.dialogResizable}
            fullWidth={fullWidth ? fullWidth : uiConf.dialogFullWidth}
            maxWidth={maxWidth ? maxWidth : uiConf.confirmDialogMaxWidth}
            onInitialized={(instance) => onDialogInitialized(instance)}
            onClose={handleClose}
            {...others}
        >
            {uiConf.modalWithCloseButton ? (
                <DialogHeaderWithClose instance={instance}>
                    <DialogTitle>{title}</DialogTitle>
                </DialogHeaderWithClose>
            ) : (
                <DialogHeader>
                    <DialogTitle>{title}</DialogTitle>
                </DialogHeader>
            )}

            <DialogContent dividers>
                <FormControl fullWidth>
                    <InputLabel id="unit-purpose-select-label">{t("label.unitPurpose", "단위 목적")}</InputLabel>
                    <Select
                        labelId="unit-purpose-select-label"
                        value={selected}
                        onChange={(e) => setSelected(e.target.value)}
                    >
                        {purposeList?.map((purpose) => (
                            <MenuItem key={purpose.unitPurposeId} value={purpose.unitPurposeId}>
                                {purpose.purposeName}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
            </DialogContent>

            <DialogActionsOk onOk={handleConfirm} onClose={handleClose} />
        </Dialog>
    );
}

export default UnitPurposeSelectDialog;
